import { useContext, useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Fab,
  Fade,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputLabel,
  ListItem,
  ListItemButton,
  ListItemText,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  ChevronLeft,
  ChevronRight,
  Delete,
  OpenInFull,
  OpenInNew,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import { set, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { decryptURL } from 'utils/functions';
import { dropCondicaoMl } from 'utils/drops';
import { DropsContext } from 'contexts/DropsContext';
import { AnuncioContext } from 'contexts/AnuncioContext';
import { useDialog } from 'components/Modals';
import VirtualDrop from 'components/VirtualDrop';
import InputMask from 'components/InputMask';
import Container from 'components/Container';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Header from 'components/Header';
import Input from 'components/Input';
import Blank from 'assets/blank.png';
import api from 'services/api';
import styles from './styles';
import { useTheme } from '@emotion/react';

const RenderInputs = ({ inputs = [], control }) => {
  return inputs.map((input) => {
    if (input?.value_type === 'string') {
      return (
        <Grid item xs={12} sm={6} key={input?.id}>
          <Tooltip title={input?.hint} placement="top">
            <Box>
              <Input name={input?.id} control={control} label={input?.name} />
            </Box>
          </Tooltip>
        </Grid>
      );
    }
    if (input?.value_type === 'number') {
      return (
        <Grid item xs={12} sm={6} key={input?.id}>
          <Tooltip title={input?.hint} placement="top">
            <Box>
              <InputMask
                name={input?.id}
                control={control}
                label={input?.name}
              />
            </Box>
          </Tooltip>
        </Grid>
      );
    }
    if (input?.value_type === 'list' || input?.value_type === 'boolean') {
      return (
        <Grid item xs={12} sm={6} key={input?.id}>
          <Tooltip title={input?.hint} placement="top">
            <Box>
              <Dropdown
                name={input?.id}
                control={control}
                label={input?.name}
                options={input?.values?.map((v) => ({
                  value: v?.name,
                  label: v?.name,
                }))}
              />
            </Box>
          </Tooltip>
        </Grid>
      );
    }
    return null;
  });
};

const renderValues = (item) => {
  let defaultValues = {};
  item?.allAttributes?.map((a) => {
    defaultValues[a.id] =
      a?.value_type === 'list' || a?.value_type === 'boolean' ? null : '';
  });
  item?.attributes?.map((a) => {
    defaultValues[a.id] =
      a?.value_type === 'list' || a?.value_type === 'boolean'
        ? a?.value_name
        : a?.value_name;
  });
  return defaultValues;
};

const Modal = ({
  item,
  callback,
  family_name,
  category_id,
  integra_access_id,
}) => {
  const defaultValues = {
    id: item?.id,
    produto_id: item?.produto_id || null,
    condition: item?.condition || null,
    descricao: item?.description || '',
    integra_access_id,
    category_id,
    family_name,
    config: null,
    preco: item?.price || '',
    saldo: item?.available_quantity || '',
    reserva: item?.reserva || 'SIM',
    ...(!item?.id && {
      listing_type_id: item?.listing_type_id || null,
      logistic_type: item?.logistic_type || null,
      garantia: item?.garantia || null,
      tempogarantia: item?.tempogarantia || '',
      especificacao: item?.especificacao || '',
      free_shipping: item?.free_shipping || false,
      local_pick_up: item?.local_pick_up || false,
    }),
  };
  const { closeDialog } = useDialog();
  const { drops } = useContext(DropsContext);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const { control, handleSubmit, setValue, watch } = useForm({ defaultValues });
  const attrForm = useForm({ defaultValues: renderValues(item) });
  const free_shipping = watch('free_shipping');
  const local_pick_up = watch('local_pick_up');
  const produto_id = watch('produto_id');
  const condition = watch('condition');
  const reserva = watch('reserva');
  const config = watch('config');
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.only('xs'));
  const sm = useMediaQuery(theme.breakpoints.only('sm'));
  const md = useMediaQuery(theme.breakpoints.only('md'));
  const lg = useMediaQuery(theme.breakpoints.only('lg'));
  const imagens = item?.pictures || [];

  const getCols = () => {
    if (xs) return 1;
    if (sm) return 2;
    if (md) return 3;
    if (lg) return 4;
    return 5;
  };

  const cols = getCols();

  const exceeded = Boolean(
    (config?.altura_prod || 0) > (config?.altura_categoria || 0) ||
    (config?.largura_prod || 0) > (config?.largura_categoria || 0) ||
    (config?.peso_prod || 0) > (config?.peso_categoria || 0)
  );

  const loadEnvio = async (values) => {
    try {
      setLoading(true);
      const payload = {
        family: true,
        produto_id: values?.produto_id,
        category_id: values?.category_id,
        condition: values?.condition,
        integra_access_id: values?.integra_access_id,
      };
      const { data } = await api.post(
        `/Integracoes/MercadoLivre/Envio`,
        payload
      );
      setValue('config', data || null);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async ({
    config,
    attributes,
    descricao,
    saldo,
    preco,
    condition,
    integra_access_id,
    category_id,
    ...rest
  }) => {
    const data = {
      ...rest,
      ...(!item?.id
        ? { descricao, preco, saldo, condition, integra_access_id, category_id }
        : { description: descricao, price: preco, available_quantity: saldo }),

      attributes: Object.entries(attributes || {}).map(([id, value_name]) => ({
        id,
        value_name,
      })),
    };
    try {
      setPostLoading(true);
      if (item?.id) {
        await api.put(`/Integracoes/MercadoLivre/Anuncio`, data);
      } else {
        await api.post(`/Integracoes/MercadoLivre/Anuncio`, data);
      }
      closeDialog();
      if (callback) callback();
    } catch (error) {
    } finally {
      setPostLoading(false);
    }
  };

  const onDeleteMidia = async (id) => {
    try {
      const pictures = imagens
        ?.filter((f) => f?.id !== id)
        ?.map((p) => ({ id: p?.id, url: p?.url }));
      const data = { id: item?.id, pictures };
      setPostLoading(true);
      await api.put(`/Integracoes/MercadoLivre/Anuncio`, data);
      closeDialog();
      if (callback) callback();
    } catch (error) {
    } finally {
      setPostLoading(false);
    }
  };

  const onChangePosition = async (index, move) => {
    try {
      const pictures = imagens?.map((p) => ({
        id: p?.id,
        url: p?.url,
      }));
      const element = pictures[index];
      pictures.splice(index, 1);
      pictures.splice(index + move, 0, element);
      const data = { id: item?.id, pictures };
      setPostLoading(true);
      await api.put(`/Integracoes/MercadoLivre/Anuncio`, data);
      closeDialog();
      if (callback) callback();
    } catch (error) {
    } finally {
      setPostLoading(false);
    }
  };

  useEffect(() => {
    if (produto_id && condition) {
      handleSubmit(loadEnvio)();
    }
  }, [produto_id, condition]);

  return (
    <Grid container spacing={2}>
      {item?.id ? (
        <Grid item xs={12}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
            <Tab value={0} label="Informações Gerais" />
            <Tab value={1} label="Imagens" />
          </Tabs>
        </Grid>
      ) : (
        <Grid item xs={12} textAlign="center">
          <Typography variant="h6">Informações Gerais</Typography>
        </Grid>
      )}
      {tab === 0 && (
        <>
          <Grid item xs={12} sm={8}>
            <VirtualDrop
              name="produto_id"
              control={control}
              label="Produto"
              options={drops?.Produto?.filter((f) =>
                [
                  '00-Mercadoria para revenda',
                  '04-Produto acabado',
                  '05-Subproduto',
                  '06-Produto intermediário',
                ]?.includes(f?.tipolinha)
              )}
              onValueChange={(v) => setValue('descricao', v?.label || '')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Dropdown
              name="condition"
              control={control}
              label="Condição"
              options={dropCondicaoMl}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputMask name="preco" control={control} label="Preço" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputMask
              name="saldo"
              label="Saldo"
              control={control}
              disabled={reserva === 'NAO'}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            display="flex"
            alignItems="center"
            justifyContent="space-evenly"
          >
            <InputLabel variant="body2">Sincronizar Saldo</InputLabel>
            <Switch
              checked={reserva === 'NAO'}
              onChange={(_, v) => setValue('reserva', v ? 'NAO' : 'SIM')}
            />
          </Grid>
          {!item?.id && (
            <>
              <Grid item xs={12} sm={6}>
                <Dropdown
                  name="listing_type_id"
                  control={control}
                  label="Tipo de Publicação"
                  options={config?.tipos_publicacao?.map((v) => ({
                    value: v?.id,
                    label: v?.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Dropdown
                  name="logistic_type"
                  control={control}
                  label="Tipo de Logística"
                  options={config?.logistic_types}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Dropdown
                  name="garantia"
                  control={control}
                  label="Garantia"
                  options={config?.garantias?.map((v) => ({
                    value: v?.name,
                    label: v?.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  name="tempogarantia"
                  control={control}
                  label="Tempo da Garantia"
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  name="especificacao"
                  control={control}
                  label="Especificação"
                  multiline
                  rows={5}
                />
              </Grid>
              {Boolean(config?.free_shipping) && (
                <Grid item xs={12} sm={6} display="flex" alignItems="center">
                  <Checkbox
                    color="primary"
                    id="free_shipping"
                    label="Frete Gráris"
                    checked={free_shipping}
                    onChange={(_, v) => setValue('free_shipping', v)}
                  />
                  <InputLabel htmlFor="free_shipping">Frete Grátis</InputLabel>
                </Grid>
              )}
              {Boolean(config?.local_pick_up) && (
                <Grid item xs={12} sm={6} display="flex" alignItems="center">
                  <Checkbox
                    color="primary"
                    id="local_pick_up"
                    checked={local_pick_up}
                    label="Retirada em Mãos"
                    onChange={(_, v) => setValue('local_pick_up', v)}
                  />
                  <InputLabel htmlFor="local_pick_up">
                    Retirada em Mãos
                  </InputLabel>
                </Grid>
              )}
              {exceeded && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="red">
                    Atenção! Dimensões do produto excedida. Você estará sujeito
                    a cobrança adicional de frete.
                  </Typography>
                </Grid>
              )}
            </>
          )}
          <RenderInputs
            inputs={config?.attributes}
            control={attrForm?.control}
          />
          <Grid item xs={12} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              loading={loading || postLoading}
              onClick={attrForm?.handleSubmit((attributes) =>
                handleSubmit((v) => onSubmit({ ...v, attributes }))()
              )}
            >
              Finalizar
            </Button>
          </Grid>
        </>
      )}
      {tab === 1 && (
        <Grid item xs={12}>
          <ImageList cols={cols} gap={10} rowHeight={200}>
            {imagens?.map((img, index) => (
              <ImageListItem key={index}>
                <Box
                  src={img.url}
                  loading="lazy"
                  component="img"
                  alt={`img-${index}`}
                  sx={styles?.image}
                />
                <ImageListItemBar
                  actionIcon={
                    <>
                      <IconButton
                        size="small"
                        sx={styles?.icon}
                        onClick={() => window.open(img?.url)}
                      >
                        <OpenInFull fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={styles?.icon}
                        disabled={loading}
                        onClick={() => onDeleteMidia(img?.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={styles?.icon}
                        disabled={index <= 0}
                        onClick={() => onChangePosition(index, -1)}
                      >
                        <ChevronLeft fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index + 1 >= imagens?.length}
                        sx={styles?.icon}
                        onClick={() => onChangePosition(index, 1)}
                      >
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Grid>
      )}
      <Fade in={loading}>
        <Backdrop open={loading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Grid>
  );
};

const ImageCarousel = ({ imagens = [], interval = 10000 }) => {
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (!imagens.length) return;

    const timer = setInterval(() => {
      setFade(false);

      setTimeout(() => {
        setIndex((prev) => (prev + 1) % imagens.length);
        setFade(true);
      }, 300);
    }, interval);

    return () => clearInterval(timer);
  }, [imagens, interval]);

  const currentImage = imagens[index]?.url || Blank;

  return (
    <Box
      component="img"
      src={currentImage}
      sx={{
        mr: 2,
        width: 100,
        height: 100,
        objectFit: 'contain',
        transition: 'opacity 0.5s ease-in-out',
        opacity: fade ? 1 : 0,
      }}
    />
  );
};

const MercadoLivre = () => {
  const { hash } = useParams();
  const decrypt = decryptURL(hash);
  const [loading, setLoading] = useState(false);
  const [anuncio, setAnuncio] = useState(null);
  const { atividadeAnuncio, postLoading } = useContext(AnuncioContext);
  const { openDialog } = useDialog();
  const variations = anuncio?.variations || [];
  const category_id = anuncio?.category_id || null;
  const family_name = anuncio?.family_name || null;
  const integra_access_id = decrypt?.integra_access_id;

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { id: decrypt?.id, integra_access_id };
      const { data } = await api.get(`/Integracoes/MercadoLivre/Anuncio`, {
        params,
      });
      setAnuncio(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onPlayPause = (e, item) => {
    e?.stopPropagation();
    const data = { codigo: item?.item_id };
    atividadeAnuncio({ data, cb: loadData });
  };

  return (
    <Container>
      <Header titulo="Detalhes do Anúncio" subtitulo={family_name} />
      {variations?.map((item, i) => {
        return (
          <Box key={i}>
            {!i && <Divider />}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() =>
                  openDialog(
                    <Modal
                      item={item}
                      callback={loadData}
                      category_id={category_id}
                      family_name={family_name}
                      integra_access_id={integra_access_id}
                    />,
                    'Editar Variação'
                  )
                }
              >
                <ImageCarousel imagens={item?.pictures || []} />
                <Box flex={1} display="flex" flexDirection="column">
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    {item?.title}
                  </Typography>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="body2" color="text.secondary">
                      Produto:{' '}
                      <Typography
                        variant="body2"
                        component="span"
                        fontWeight="bold"
                      >
                        {item?.produto_descricao || '-'}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Código:{' '}
                      <Typography
                        variant="body2"
                        component="span"
                        fontWeight="bold"
                      >
                        {item?.item_id || '-'}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status:{' '}
                      <Typography
                        variant="body2"
                        component="span"
                        fontWeight="bold"
                        color={
                          item?.ativo === 'SIM' ? 'success.main' : 'error.main'
                        }
                      >
                        {item?.ativo === 'SIM' ? 'Ativo' : 'Inativo'}
                      </Typography>
                    </Typography>
                    <Box display="flex" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Saldo do Produto:{' '}
                        <Typography
                          variant="body2"
                          component="span"
                          fontWeight="bold"
                        >
                          {item?.saldo_produto || 0}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Quantidade Anúnciada:{' '}
                        <Typography
                          variant="body2"
                          component="span"
                          fontWeight="bold"
                        >
                          {item?.available_quantity || 0}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Typography
                  ml={2}
                  variant="h6"
                  fontWeight="bold"
                  color="primary.main"
                >
                  {item?.price?.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </Typography>
                <IconButton
                  sx={{ ml: 2 }}
                  onClick={(e) => onPlayPause(e, item)}
                >
                  {item?.ativo === 'SIM' ? <Pause /> : <PlayArrow />}
                </IconButton>
                {item?.permalink && (
                  <IconButton
                    sx={{ ml: 2 }}
                    onClick={(e) => {
                      e?.stopPropagation();
                      window.open(item?.permalink, '_blank');
                    }}
                  >
                    <OpenInNew />
                  </IconButton>
                )}
              </ListItemButton>
            </ListItem>
            <Divider />
          </Box>
        );
      })}
      <Fade in={loading}>
        <Backdrop open={loading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
      <Fab
        color="primary"
        sx={styles?.fab}
        onClick={() => {
          const baseVariation = variations?.[0]
            ? (({ id, ...rest }) => rest)(variations[0])
            : null;

          openDialog(
            <Modal
              callback={loadData}
              item={baseVariation}
              category_id={category_id}
              family_name={family_name}
              integra_access_id={integra_access_id}
            />,
            'Adicionar Variação'
          );
        }}
      >
        <Add />
      </Fab>
    </Container>
  );
};

export default MercadoLivre;
